import './polyfills';
import {UNLOGGED_USER_BLACKLIST_DOMAIN, USER_INT} from './constants';
import setPublicAPI from './publicAPI';
import {
  openImmediately,
  signedUserHasConsentCookie,
  signedUserNoConsentCookie,
  userHasConsentCookie,
  userNoConsentCookie
} from './services/cmp';
import {existsConsentCookie, hasEuconsentAlready} from './services/consent';
import {copyTcf2FromGlobalStorage, hasEuconsentV2, signedUserHasTcf2ConsentCookie} from './services/cmp2';
import {openLoggedDialog} from './services/dialog';
import {logger} from './services/logger';
import setPaws from './services/paws';
import {getProfileData, setRusId} from './services/profil';
import {initRedirect, shouldRedirect, inRedirectWhitelist} from './services/redirect';
import {isUserSigned} from './services/user';
import {autoOpen, inProfile} from './utils/scriptParams';
import {isStorageEnabled, isCapableStoreAndHoldCookies, isApplePlatform} from './utils/checks';
import {demoMode} from './utils/urlParams';
import {isWebview} from './utils/webview';
import {config, end, start, initSentry} from './services/measurement';

const openInternalImmediately = openImmediately(USER_INT);

const initSignedInUser = async () => {
  setPaws(true);

  try {
    const profileData = await getProfileData();
    logger(300, 'Data from Profile received:', profileData);

    const {rusId} = profileData;
    config(USER_INT, rusId);
    setRusId(rusId);

    /* Temporary for switching to CMP2 */
    if (hasEuconsentV2(profileData)) {
      signedUserHasTcf2ConsentCookie(profileData, USER_INT);
      return;
    }
    /* end of temporary switching to CMP2 */

    if (hasEuconsentAlready(profileData)) {
      signedUserHasConsentCookie(profileData, USER_INT);
    } else {
      signedUserNoConsentCookie(profileData, USER_INT);
    }
  } catch (error) {
    logger(301, 'No profile data received.');
  }
};

const initSignedOutUser = () => {
  config(USER_INT);
  setPaws(false);

  if (existsConsentCookie()) {
    logger(303, 'Euconsent cookie found in local domain.');
    userHasConsentCookie(USER_INT);
  } else {
    logger(304, 'No euconsent cookie found in local domain.');
    userNoConsentCookie(USER_INT);
  }
};

const openInProfilePage = async () => {
  logger(305, 'Dialog was initializated with click.');
  logger(306, 'Opening in profile page');

  try {
    const profileData = await getProfileData();
    logger(307, 'Data from Profile received:', profileData);

    const {rusId} = profileData;
    config(USER_INT, rusId);
    setRusId(rusId);

    openLoggedDialog(profileData, USER_INT);
  } catch (error) {
    logger(308, 'No profile data received.');
  }
};

const initCMP = async () => {
  logger(309, 'Init CMP dialog');

  if (await isUserSigned()) {
    logger(310, 'Signed In');

    /* Hack for cypress so it knows user is logged in */
    window.scmp.loggedIn = true;

    initSignedInUser();
  } else {
    logger(311, 'Signed Out');

    /* Hack for cypress so it knows user is logged out */
    window.scmp.loggedIn = false;

    /* Temporary for switching to CMP2 */
    const tcf2Copied = await copyTcf2FromGlobalStorage(USER_INT);

    if (tcf2Copied) {
      // TCF2.0 successfully copied to local domain
      end();
      return;
    }
    /* end of temporary switching to CMP2 */

    if (isApplePlatform && !inRedirectWhitelist) {
      logger(319, 'This is an Apple device or domain is not whitelisted, do not continue.');
      return;
    }

    if (!isCapableStoreAndHoldCookies) {
      logger(312, 'Cannot store and hold cookies');
      return;
    }

    const host = window.location.hostname.replace('www.', '');

    if (UNLOGGED_USER_BLACKLIST_DOMAIN.includes(host)) {
      logger(313, 'This domain is blocked.');
      end();
      return;
    } else if (shouldRedirect()) {
      initRedirect(USER_INT);
      return;
    } else {
      initSignedOutUser();
    }
  }
};

/**
 /=============================================================================
 /
 / CMP main block where the fun begins.
 /
 /=============================================================================
 */

initSentry();
config(USER_INT, null, true);
start();

window.scmp = {
  open() {
    if (inProfile()) {
      openInProfilePage();
    } else {
      openInternalImmediately();
    }
  },
  openImmediately: openInternalImmediately,
  openInProfilePage,
  setPublicAPI,
  redirect: () => initRedirect(USER_INT),
  _redirectDev: () => initRedirect(USER_INT, true),
  sznCookie: true
};

const autoInit = autoOpen();
logger(315, 'AutoOpen: ' + autoInit);

if (demoMode()) {
  openInternalImmediately();
  logger(316, 'Demo dialog initialization.');
} else if (isStorageEnabled && autoInit && !isWebview(USER_INT)) {
  initCMP();
  logger(317, 'Automatic dialog initialization.');
} else {
  logger(318, 'Auto init canceled');
}

// Sets public API required by IAB.
if (!inProfile()) {
  setPublicAPI();
}
